import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { frontMiddleBack } from "../lib/greenFMB";

// ---- helpers --------------------------------------------------------------
const METERS_TO_YARDS = 1.0936133;
const toRad = (d) => (d * Math.PI) / 180;
function distanceYds(a, b) {
  if (!a || !b) return null;
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  const m = R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  return Math.round(m * METERS_TO_YARDS);
}

// ---- custom markers -------------------------------------------------------
const pinIcon = L.divIcon({
  className: "",
  html: `<div style="display:flex;flex-direction:column;align-items:center;transform:translateY(-100%)">
    <div style="background:#143D2F;color:#FFE45E;font-weight:900;font-size:11px;padding:3px 7px;border-radius:9999px;letter-spacing:0.05em;text-transform:uppercase;box-shadow:0 2px 6px rgba(0,0,0,0.4);white-space:nowrap">Pin</div>
    <div style="width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-top:6px solid #143D2F;margin-top:-1px"></div>
  </div>`,
  iconSize: [30, 24],
  iconAnchor: [15, 24],
});

const playerIcon = L.divIcon({
  className: "",
  html: `<div style="position:relative;width:20px;height:20px">
    <div style="position:absolute;inset:-6px;border-radius:50%;background:rgba(59,130,246,0.25);animation:fairwayPulse 2s ease-out infinite"></div>
    <div style="position:relative;width:20px;height:20px;border-radius:50%;background:#3B82F6;border:3px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.4)"></div>
    <div style="position:absolute;left:50%;top:-24px;transform:translateX(-50%);background:#3B82F6;color:#fff;font-weight:900;font-size:10px;padding:2px 7px;border-radius:9999px;letter-spacing:0.05em;text-transform:uppercase;box-shadow:0 2px 6px rgba(0,0,0,0.4);white-space:nowrap;font-family:'Inter',system-ui,sans-serif">You</div>
  </div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const targetIcon = L.divIcon({
  className: "",
  html: `<div style="width:28px;height:28px;border:2px solid #FFE45E;border-radius:50%;background:rgba(255,228,94,0.18);box-shadow:0 0 0 1px rgba(0,0,0,0.4);position:relative">
    <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center">
      <div style="width:6px;height:6px;border-radius:50%;background:#FFE45E"></div>
    </div>
    <div style="position:absolute;top:50%;left:-6px;width:4px;height:2px;background:#FFE45E"></div>
    <div style="position:absolute;top:50%;right:-6px;width:4px;height:2px;background:#FFE45E"></div>
    <div style="position:absolute;left:50%;top:-6px;width:2px;height:4px;background:#FFE45E"></div>
    <div style="position:absolute;left:50%;bottom:-6px;width:2px;height:4px;background:#FFE45E"></div>
  </div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

// One-time inject pulse keyframes for the player marker. Use textContent
// (not innerHTML) so static-analysis linters don't flag this as XSS — the
// content is a fixed CSS keyframe string with no user input.
if (typeof document !== "undefined" && !document.getElementById("fairway-map-keyframes")) {
  const s = document.createElement("style");
  s.id = "fairway-map-keyframes";
  s.textContent = `@keyframes fairwayPulse{0%{transform:scale(0.7);opacity:0.85}80%,100%{transform:scale(1.6);opacity:0}}`;
  document.head.appendChild(s);
}

// Tiny F/B (green edge) marker
function greenEdgeIcon(letter, color) {
  return L.divIcon({
    className: "",
    html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};color:#fff;font-weight:900;font-size:10px;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.4);font-family:'Inter',system-ui,sans-serif">${letter}</div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

// ---- main component ------------------------------------------------------
/**
 * Aerial satellite view of the current hole.
 * - Satellite tiles: Esri World Imagery (free, no API key, attribution required).
 * - Markers: player (live GPS, blue pulse), pin (course data), target (tap to drop).
 * - Distance bubbles render on the polyline midpoints.
 */
export default function HoleView({
  playerPos,
  breadcrumb,
  pin,
  holeNumber,
  par,
  cardYards,
  onClose,
  // Optional: when provided, shows a "Submit pin" button. The function
  // receives {lat, lng} of the tapped target. Used by the in-round flow
  // for placeholder courses so users can fix the green location with
  // one tap on the satellite view.
  onSubmitPin,
}) {
  const [target, setTarget] = useState(null); // {lat,lng}
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState("");
  const mapRef = useRef(null);

  const tee = playerPos; // We don't have stored tee coords; the live position acts as tee

  // Reset target when the hole changes
  useEffect(() => {
    setTarget(null);
  }, [holeNumber]);

  // Auto-fit bounds whenever the key markers change
  const bounds = useMemo(() => {
    const pts = [];
    if (tee) pts.push([tee.lat, tee.lng]);
    if (pin) pts.push([pin.lat, pin.lng]);
    if (target) pts.push([target.lat, target.lng]);
    if (pts.length === 0) return null;
    if (pts.length === 1) {
      const [lat, lng] = pts[0];
      return L.latLngBounds([lat - 0.001, lng - 0.001], [lat + 0.001, lng + 0.001]);
    }
    return L.latLngBounds(pts);
  }, [tee, pin, target]);

  useEffect(() => {
    if (!mapRef.current || !bounds) return;
    mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 18 });
  }, [bounds]);

  // Default center for the initial render (pin coords)
  const initialCenter = pin ? [pin.lat, pin.lng] : [54.5, -2.5];

  const distToPin = distanceYds(tee, pin);
  const distToTarget = distanceYds(tee, target);
  const targetToPin = distanceYds(target, pin);
  const midpoint = (a, b) =>
    a && b ? { lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 } : null;
  const midToTarget = midpoint(tee, target);
  const midTargetToPin = midpoint(target, pin);
  const midPlayerToPin = midpoint(tee, pin);

  // Front / Middle / Back approximation
  const fmb =
    tee && pin && !target
      ? frontMiddleBack(tee.lat, tee.lng, pin.lat, pin.lng)
      : null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black"
      data-testid="hole-view"
      style={{ touchAction: "manipulation" }}
    >
      {/* Map */}
      <MapContainer
        center={initialCenter}
        zoom={17}
        style={{ width: "100%", height: "100%" }}
        zoomControl={false}
        attributionControl={false}
        ref={mapRef}
      >
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          attribution="Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics"
          maxNativeZoom={19}
          maxZoom={20}
        />

        <TapToTarget onTap={(latlng) => setTarget({ lat: latlng.lat, lng: latlng.lng })} />

        {pin && <Marker position={[pin.lat, pin.lng]} icon={pinIcon} />}
        {/* Breadcrumb trail — where the player has walked on this hole. */}
        {breadcrumb && breadcrumb.length >= 2 && (
          <Polyline
            positions={breadcrumb.map((p) => [p.lat, p.lng])}
            pathOptions={{ color: "#60A5FA", weight: 3, opacity: 0.7, dashArray: "2 6", lineCap: "round" }}
          />
        )}
        {tee && <Marker position={[tee.lat, tee.lng]} icon={playerIcon} />}
        {target && (
          <Marker
            position={[target.lat, target.lng]}
            icon={targetIcon}
            eventHandlers={{ click: () => setTarget(null) }}
          />
        )}

        {/* Front / Back green markers (small) when no target is set */}
        {fmb && (
          <>
            <Marker
              position={[fmb.frontPos.lat, fmb.frontPos.lng]}
              icon={greenEdgeIcon("F", "#22c55e")}
              interactive={false}
            />
            <Marker
              position={[fmb.backPos.lat, fmb.backPos.lng]}
              icon={greenEdgeIcon("B", "#0ea5e9")}
              interactive={false}
            />
          </>
        )}

        {/* Polylines */}
        {tee && pin && !target && (
          <Polyline
            positions={[
              [tee.lat, tee.lng],
              [pin.lat, pin.lng],
            ]}
            pathOptions={{ color: "#FFE45E", weight: 3, opacity: 0.9, dashArray: "6 6" }}
          />
        )}
        {tee && target && (
          <Polyline
            positions={[
              [tee.lat, tee.lng],
              [target.lat, target.lng],
            ]}
            pathOptions={{ color: "#3B82F6", weight: 3, opacity: 0.9 }}
          />
        )}
        {target && pin && (
          <Polyline
            positions={[
              [target.lat, target.lng],
              [pin.lat, pin.lng],
            ]}
            pathOptions={{ color: "#FFE45E", weight: 3, opacity: 0.9, dashArray: "6 6" }}
          />
        )}

        {/* Distance bubbles */}
        {!target && midPlayerToPin && distToPin != null && (
          <DistanceBubble pos={midPlayerToPin} value={distToPin} variant="pin" testid="hole-view-dist-pin" />
        )}
        {target && midToTarget && distToTarget != null && (
          <DistanceBubble pos={midToTarget} value={distToTarget} variant="target" testid="hole-view-dist-target" />
        )}
        {target && midTargetToPin && targetToPin != null && (
          <DistanceBubble pos={midTargetToPin} value={targetToPin} variant="pin" testid="hole-view-dist-remaining" />
        )}
      </MapContainer>

      {/* Top header overlay */}
      <div className="absolute top-0 left-0 right-0 z-[1000] pointer-events-none">
        <div className="flex items-start justify-between p-3 pointer-events-auto">
          <button
            type="button"
            onClick={onClose}
            data-testid="hole-view-close"
            aria-label="Back to numbers view"
            className="w-10 h-10 rounded-full bg-black/60 backdrop-blur-md text-white flex items-center justify-center shadow-lg"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>

          <div
            className="bg-black/70 backdrop-blur-md text-white rounded-2xl px-4 py-2 shadow-lg text-center"
            data-testid="hole-view-header"
          >
            <div className="font-heading font-black text-base leading-none">
              {holeNumber}<sup className="text-[10px] font-bold ml-0.5">{ordinal(holeNumber)}</sup>
            </div>
            <div className="text-[10px] uppercase tracking-wider opacity-80 mt-1">
              Par {par} · {cardYards} yds
            </div>
          </div>

          {target ? (
            <button
              type="button"
              onClick={() => setTarget(null)}
              data-testid="hole-view-clear-target"
              className="bg-black/60 backdrop-blur-md text-[#FFE45E] rounded-full px-3 h-10 font-bold text-xs uppercase tracking-wider shadow-lg"
            >
              Clear pin
            </button>
          ) : (
            <div className="w-10" />
          )}
        </div>
      </div>

      {/* Bottom hint + F/M/B strip */}
      <div className="absolute bottom-3 left-0 right-0 z-[1000] flex flex-col items-center gap-2 pointer-events-none">
        {fmb && (
          <div
            data-testid="hole-view-fmb"
            className="bg-black/70 backdrop-blur-md rounded-2xl px-3 py-2 flex items-center gap-3 shadow-lg pointer-events-auto"
          >
            {[
              { label: "F", val: fmb.front, color: "#22c55e" },
              { label: "M", val: fmb.middle, color: "#FFE45E" },
              { label: "B", val: fmb.back, color: "#0ea5e9" },
            ].map((s) => (
              <div key={s.label} className="flex items-center gap-1.5" data-testid={`hole-view-fmb-${s.label.toLowerCase()}`}>
                <span
                  className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded"
                  style={{ background: s.color, color: "#143D2F" }}
                >
                  {s.label}
                </span>
                <span className="font-mono-num font-black text-white text-base leading-none">
                  {s.val}
                </span>
              </div>
            ))}
          </div>
        )}
        <div className="bg-black/70 backdrop-blur-md text-white text-[11px] uppercase tracking-wider rounded-full px-3 py-1.5 shadow-lg pointer-events-auto">
          {onSubmitPin
            ? (target ? "Tap Submit to use this as the real pin" : "Tap the actual green on the map")
            : (target ? "Tap the target to clear · drag map to pan" : "Tap anywhere to drop a target")}
        </div>
        {onSubmitPin && target && (
          <button
            type="button"
            disabled={submitting}
            onClick={async () => {
              setSubmitting(true);
              setSubmitMsg("");
              try {
                const res = await onSubmitPin({ lat: target.lat, lng: target.lng });
                setSubmitMsg(
                  res && res.auto_approved
                    ? "✓ Pin updated — thanks!"
                    : "✓ Pin submitted for review — thanks!"
                );
                setTimeout(() => onClose && onClose(), 1500);
              } catch (e) {
                setSubmitMsg(e?.response?.data?.detail || "Couldn't submit. Try again.");
                setSubmitting(false);
              }
            }}
            data-testid="hole-view-submit-pin"
            className="pointer-events-auto bg-amber-500 text-white text-xs font-black uppercase tracking-wider rounded-full px-4 py-2 shadow-lg active:scale-95 transition-transform"
          >
            {submitting ? "Submitting…" : "⚑ Submit this as the pin"}
          </button>
        )}
        {submitMsg && (
          <div
            data-testid="hole-view-submit-msg"
            className={`pointer-events-auto rounded-full px-3 py-1.5 text-[11px] font-bold shadow-lg ${submitMsg.startsWith("✓") ? "bg-green-500 text-white" : "bg-red-500 text-white"}`}
          >
            {submitMsg}
          </div>
        )}
      </div>
    </div>
  );
}

function TapToTarget({ onTap }) {
  useMapEvents({
    click: (e) => onTap(e.latlng),
  });
  return null;
}

function DistanceBubble({ pos, value, variant, testid }) {
  const bg = variant === "target" ? "#3B82F6" : "#143D2F";
  const fg = variant === "target" ? "#fff" : "#FFE45E";
  // Use a Marker with divIcon for the bubble
  const icon = L.divIcon({
    className: "",
    html: `<div data-testid="${testid}" style="background:${bg};color:${fg};font-weight:900;font-size:13px;font-variant-numeric:tabular-nums;padding:4px 9px;border-radius:9999px;border:2px solid rgba(255,255,255,0.85);box-shadow:0 2px 8px rgba(0,0,0,0.45);white-space:nowrap;font-family:'Inter',system-ui,sans-serif">${value}</div>`,
    iconSize: [40, 24],
    iconAnchor: [20, 12],
  });
  return <Marker position={[pos.lat, pos.lng]} icon={icon} interactive={false} />;
}

function ordinal(n) {
  if (n == null) return "";
  const s = ["TH", "ST", "ND", "RD"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}
